import type { Order, OrderInput, OrderItemInput, OrderStatus, OrderWithDetail, StatusLogEntry } from "@/features/orders/types";
import { createIdempotencyKey } from "@/shared/lib/idempotency";
import { api } from "@/shared/lib/api";
import { useOfflineQueue } from "@/shared/offline/offlineQueue";
import type { PaymentOrderOption } from "@/features/payments/paymentOptions";

export type OrderWithClient = Order & {
  clients: { name: string; phone?: string | null } | null;
  assigned_staff: { id: string; name: string; phone?: string | null; role?: string | null } | null;
  item_count?: number;
};

export async function listOrders(status?: OrderStatus | "active"): Promise<OrderWithClient[]> {
  return api.get<OrderWithClient[]>("orders", "list", { status: status ?? "" });
}

export async function listUnpaidOrders(): Promise<PaymentOrderOption[]> {
  return api.get<PaymentOrderOption[]>("orders", "unpaid-list");
}

export async function getOrderWithDetail(id: string): Promise<OrderWithDetail> {
  return api.get<OrderWithDetail>("orders", "get", { id });
}

export async function listStatusLog(orderId: string): Promise<StatusLogEntry[]> {
  return api.get<StatusLogEntry[]>("orders", "status-log", { order_id: orderId });
}

export async function createOrder(input: OrderInput): Promise<Order | null> {
  const idempotencyKey = createIdempotencyKey("order");

  if (!navigator.onLine) {
    await useOfflineQueue.getState().enqueue({
      id: idempotencyKey,
      type: "order.create",
      payload: input,
      createdAt: new Date().toISOString(),
      attempts: 0
    });
    return null;
  }

  return createOrderOnline(input, idempotencyKey);
}

export async function createOrderOnline(input: OrderInput, idempotencyKey: string): Promise<Order> {
  return api.post<Order>("orders", "create", {
    idempotencyKey,
    clientId: input.clientId,
    title: input.title,
    garmentType: input.garmentType,
    totalPriceKobo: input.totalPriceKobo,
    deliveryDate: input.deliveryDate,
    fabricNotes: input.fabricNotes ?? null,
    assignedStaffId: input.assignedStaffId ?? null,
    items: input.items,
    templateId: input.templateId ?? null,
  });
}

export async function updateOrder(input: {
  id: string;
  title?: string;
  garmentType?: string;
  deliveryDate?: string;
  fabricNotes?: string;
}): Promise<Order> {
  return api.post<Order>("orders", "update", input);
}

export async function updateOrderItems(input: {
  orderId: string;
  deliveryDate?: string;
  items: OrderItemInput[];
}): Promise<{ success: boolean; totalPriceKobo: number }> {
  return api.post<{ success: boolean; totalPriceKobo: number }>("orders", "update-items", input);
}

export async function updateOrderItemStatus(input: { orderId: string; itemId: string; status: OrderStatus }): Promise<void> {
  await api.post("orders", "update-item-status", input);
}

export async function assignOrderItemStaff(input: { orderId: string; itemId: string; staffId: string; commissionKobo?: number }): Promise<{ success: boolean; staffName?: string; commissionKobo?: number }> {
  return api.post("orders", "assign-item-staff", input);
}

export async function advanceOrderItemTemplateStage(input: { orderId: string; itemId: string }): Promise<{ success: boolean; stageIndex: number; completed: boolean }> {
  return api.post("orders", "advance-item-template-stage", input);
}

export async function updateOrderStatus(input: { orderId: string; status: OrderStatus }) {
  const idempotencyKey = createIdempotencyKey("status");

  if (!navigator.onLine) {
    await useOfflineQueue.getState().enqueue({
      id: idempotencyKey,
      type: "order.status",
      payload: input,
      createdAt: new Date().toISOString(),
      attempts: 0
    });
    return;
  }

  await updateOrderStatusOnline(input);
}

export async function updateOrderStatusOnline(input: { orderId: string; status: OrderStatus }) {
  await api.post("orders", "update-status", { orderId: input.orderId, status: input.status });
}

export async function markOrderDelivered(orderId: string): Promise<void> {
  await api.post("orders", "mark-delivered", { orderId });
}
